// Angular
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject, DOCUMENT } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { SwPush, SwUpdate, VersionReadyEvent } from '@angular/service-worker';
// Service
import { ThemeModeService } from './layout/components/sidebar/theme-mode.service';
import { SplashScreenService } from './layout/components/splash-screen/splash-screen.service';
import { AuthService } from '@auth-service';
// Rxjs
import { Subscription, filter, noop } from 'rxjs';
// Components
import { GoogleAnalyticsGTagComponent } from './core/components/google-analytics-gtag/google-analytics-gtag.component';
import { SplashScreenComponent } from './layout/components/splash-screen/splash-screen.component';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'body[app-root]',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterOutlet, GoogleAnalyticsGTagComponent, SplashScreenComponent]
})
export class AppComponent implements OnInit, OnDestroy {

  // Private properties
  private readonly router = inject(Router);
  private modeService = inject(ThemeModeService);
  private splashScreenService = inject(SplashScreenService);
  private readonly swPush = inject(SwPush);
  private readonly swUpdate = inject(SwUpdate);
  private readonly authService = inject(AuthService);
  private document = inject<Document>(DOCUMENT);

  // Subscription
  private unsubscribe: Subscription[] = [];

  /**
   * Creates an instance of AppComponent.
   * @memberof AppComponent
   */
  constructor() {
    this.setFaviconBasedOnDomain();
  }

  /**
   * OnInit
   * @memberof AppComponent
   */
  ngOnInit(): void {
    this.modeService.init();

    const routerSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(event => {
      // Hide splash screen
      this.splashScreenService.hide({ delay: 80, duration: 250 });

      // Scroll to top on every route change
      window.scrollTo(0, 0);
    });
    this.unsubscribe.push(routerSubscription);

    // Service Worker
    this.swUpdate.isEnabled ? this.handleNewAppVersionUpdate() : noop();
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    this.unsubscribe.forEach(sb => sb.unsubscribe());
    this.authService.flushRoleAndPermissions();
  }

  /**
   * Set Favicon Based on Domain
   * @private
   * @memberof AppComponent
   */
  private setFaviconBasedOnDomain(): void {
    const hostname = window.location.hostname;
    const subdomain = this.getSubdomain(hostname);
    if (subdomain) {
      const faviconSrc = `assets/media/logos/${subdomain}/favicon.ico`;
      const favicon: HTMLLinkElement = this.document.querySelector('#appFavicon') as HTMLLinkElement;
      favicon.setAttribute('href', faviconSrc);
    }
  }

  /**
   * Get Subdomain
   * @private
   * @param {string} hostname
   * @returns {(string)}
   * @memberof AppComponent
   */
  private getSubdomain(hostname: string): string {
    const parts = hostname.split('.');
    if (parts.length >= 3) {
      return parts[1];
    }
    return 'bildelsbasen';
  }

  /**
   * Informing the user that a new version is available
   * For long-running SPA applications that the user might have opened for hours,
   * we might want to check periodically to see if there is a new version of the application on the server and install it in the background.
   */
  private handleNewAppVersionUpdate(): void {
    try {
      this.swUpdate.versionUpdates.pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY')).subscribe(() => {
          if (confirm('New version available. Load New Version?')) {
            this.swUpdate.activateUpdate().then(() => window.location.reload());
          }
        });
    } catch (error) {
      console.log('Error handleNewAppVersionUpdate', error);
    }

  }

}
